import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Baby, FileText, DollarSign, Users,
  TrendingUp, Clock, AlertCircle, Plus,
  ArrowRight, CheckCircle, CalendarClock, Inbox, Upload,
} from 'lucide-react';
import { facturesApi, devisApi, echeancesApi, planningApi } from '../utils/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { setPendingFile } from './Depot/depotShared';
import { formatMontant, getStatutBadgeClass, getStatutLabel } from '../utils/calculs';

// Encadré « enfants présents aujourd'hui » par structure (clic sur un nom → sa fiche)
function PresentsAujourdhui() {
  const { structures } = useApp();
  const [data, setData] = useState({});
  useEffect(() => {
    let alive = true;
    Promise.all((structures || []).map(s =>
      planningApi.getAujourdhui(s.id).then(r => [s.id, r.data]).catch(() => [s.id, null])
    )).then(entries => { if (alive) setData(Object.fromEntries(entries)); });
    return () => { alive = false; };
  }, [structures]);
  if (!structures?.length) return null;
  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><Baby size={16} className="text-emerald-600" /> Enfants présents aujourd'hui</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {structures.map(s => {
          const d = data[s.id];
          return (
            <div key={s.id} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.couleur }} />
                  <span className="text-sm font-medium text-gray-700 truncate">{s.nom}</span>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">{d ? d.count : '…'}</span>
              </div>
              {!d ? <div className="text-xs text-gray-300">Chargement…</div>
                : !d.ouvert ? <div className="text-xs text-gray-400">Crèche fermée aujourd'hui.</div>
                  : d.enfants.length === 0 ? <div className="text-xs text-gray-400">Aucun enfant aujourd'hui.</div>
                    : <ul className="space-y-1 max-h-56 overflow-y-auto">
                        {d.enfants.map(e => (
                          <li key={e.id} className="text-[13px] flex items-center justify-between gap-2">
                            <Link to={`/enfants/${e.id}`} className="text-gray-700 hover:text-navy-700 hover:underline truncate">{e.label}</Link>
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">{e.debut}–{e.fin}</span>
                          </li>
                        ))}
                      </ul>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Encadré « professionnels présents aujourd'hui » par structure (planning équipe)
function ProsAujourdhui() {
  const { structures } = useApp();
  const [data, setData] = useState({});
  useEffect(() => {
    let alive = true;
    Promise.all((structures || []).map(s =>
      planningApi.getEquipeAujourdhui(s.id).then(r => [s.id, r.data]).catch(() => [s.id, null])
    )).then(entries => { if (alive) setData(Object.fromEntries(entries)); });
    return () => { alive = false; };
  }, [structures]);
  if (!structures?.length) return null;
  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2"><Users size={16} className="text-navy-600" /> Professionnels présents aujourd'hui</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {structures.map(s => {
          const d = data[s.id];
          return (
            <div key={s.id} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.couleur }} />
                  <span className="text-sm font-medium text-gray-700 truncate">{s.nom}</span>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-navy-100 text-navy-700 flex-shrink-0">{d ? d.count : '…'}</span>
              </div>
              {!d ? <div className="text-xs text-gray-300">Chargement…</div>
                : d.count === 0 ? <div className="text-xs text-gray-400">Aucun planning équipe aujourd'hui.</div>
                  : <ul className="space-y-1 max-h-56 overflow-y-auto">
                      {d.professionnels.map((p, i) => (
                        <li key={p.id || i} className="text-[13px] flex items-center justify-between gap-2">
                          <span className="text-gray-700 truncate">{p.label}{p.poste && <span className="text-[11px] text-gray-400"> · {p.poste}</span>}</span>
                          <span className="text-[11px] text-gray-400 whitespace-nowrap">{p.debut}–{p.fin}</span>
                        </li>
                      ))}
                    </ul>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color, to }) {
  const card = (
    <div className="card p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500 mt-0.5">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

export default function Dashboard() {
  const { getApiParams } = useApp();
  const { canSeeFinancials, can, user } = useAuth();
  const prenom = (user?.nom || '').trim().split(/\s+/)[0] || '';
  const navigate = useNavigate();
  const depotRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const deposer = (file) => { if (file) { setPendingFile(file); navigate('/depot'); } };
  const [stats, setStats] = useState(null);
  const [recentDevis, setRecentDevis] = useState([]);
  const [ech, setEch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = getApiParams();
    Promise.all([
      facturesApi.getStats(params),
      devisApi.getAll({ ...params, statut: 'en_cours' }),
    ])
      .then(([statsRes, devisRes]) => {
        setStats(statsRes.data);
        setRecentDevis(devisRes.data.slice(0, 5));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    if (can('/echeances')) {
      echeancesApi.getResume(params).then(r => setEch(r.data)).catch(() => {});
    }
  }, [getApiParams, can]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Chargement...</div>
      </div>
    );
  }

  const currentMonth = new Date().toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* En-tête majordome */}
      <div>
        <div className="text-[11px] tracking-[0.16em] uppercase text-gold-700 font-semibold mb-1">Votre majordome au quotidien</div>
        <h1 className="font-serif text-[28px] leading-tight text-navy-900">Bonjour {prenom || 'à vous'} <span className="align-middle">🎀</span></h1>
        <p className="text-navy-400 text-[14px] mt-1">Voici ce que Georges peut préparer pour vous aujourd'hui.</p>
      </div>

      {/* Alerte échéances */}
      {ech && (ech.urgent > 0 || ech.depasse > 0) && (
        <Link to="/echeances" className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl shadow-sm px-5 py-3.5 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <CalendarClock size={20} className="text-red-500" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">
              {ech.depasse > 0 && <span className="text-red-600">{ech.depasse} échéance{ech.depasse > 1 ? 's' : ''} dépassée{ech.depasse > 1 ? 's' : ''}</span>}
              {ech.depasse > 0 && ech.urgent > 0 && <span className="text-gray-400"> · </span>}
              {ech.urgent > 0 && <span className="text-amber-600">{ech.urgent} dans les 30 jours</span>}
            </div>
            <div className="text-xs text-gray-400">À traiter dans le suivi des échéances</div>
          </div>
          <ArrowRight size={16} className="text-gray-300" />
        </Link>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Baby}
          label="Enfants actifs"
          value={stats?.enfantsActifs ?? 0}
          color="bg-primary-500"
          to="/enfants"
        />
        <KPICard
          icon={FileText}
          label="Devis en cours"
          value={stats?.devisEnCours ?? 0}
          color="bg-brand-500"
          to="/devis"
        />
        {canSeeFinancials && (
          <KPICard
            icon={DollarSign}
            label={`CA ${currentMonth}`}
            value={formatMontant(stats?.moisEnCours ?? 0)}
            sub={`Taux recouvrement : ${stats?.tauxRecouvrement ?? 0}%`}
            color="bg-green-500"
            to="/finance"
          />
        )}
        <KPICard
          icon={Users}
          label="Employés"
          value={stats?.employes ?? 0}
          color="bg-navy-500"
          to="/rh"
        />
      </div>

      {/* Finance summary */}
      {stats && canSeeFinancials && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-green-500" />
              <span className="text-sm font-semibold text-gray-700">Encaissé (année)</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{formatMontant(stats.totalEncaisse)}</div>
            <div className="text-xs text-gray-400 mt-1">sur {formatMontant(stats.totalFacture)} facturé</div>
          </div>
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-orange-500" />
              <span className="text-sm font-semibold text-gray-700">En attente</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">
              {formatMontant((stats.totalFacture || 0) - (stats.totalEncaisse || 0) - (stats.totalRetard || 0))}
            </div>
            <div className="text-xs text-gray-400 mt-1">à encaisser</div>
          </div>
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm font-semibold text-gray-700">En retard</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{formatMontant(stats.totalRetard)}</div>
            <div className="text-xs text-gray-400 mt-1">à relancer</div>
          </div>
        </div>
      )}

      {/* Enfants présents aujourd'hui (par structure) */}
      <PresentsAujourdhui />

      {/* Professionnels présents aujourd'hui (par structure) */}
      <ProsAujourdhui />

      {/* Quick actions + Recent devis */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Actions rapides</h2>
          <div className="flex flex-col gap-2">
            <Link to="/enfants/nouveau" className="btn-primary justify-center">
              <Plus size={16} /> Nouvel enfant
            </Link>
            <Link to="/devis/nouveau" className="btn-secondary justify-center">
              <Plus size={16} /> Nouveau devis
            </Link>
            {canSeeFinancials && (
              <Link to="/finance" className="btn-secondary justify-center">
                <DollarSign size={16} /> Gérer la facturation
              </Link>
            )}
            <Link to="/rh" className="btn-secondary justify-center">
              <Users size={16} /> Ressources humaines
            </Link>
          </div>

          {can('/depot') && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-[12.5px] font-semibold text-gray-600 mb-2 flex items-center gap-1.5"><Inbox size={14} className="text-gold-600" /> Boîte de dépôt</div>
              <div
                onClick={() => depotRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); deposer(e.dataTransfer.files?.[0]); }}
                className={`cursor-pointer rounded-xl border-2 border-dashed px-3 py-4 text-center transition-colors ${dragOver ? 'border-gold-400 bg-gold-50' : 'border-cream-300 hover:border-gold-300 hover:bg-cream-50'}`}
              >
                <Upload size={18} className="text-navy-300 mx-auto mb-1" />
                <div className="text-[12.5px] text-navy-500">Glissez un document ou cliquez</div>
                <div className="text-[11px] text-navy-300 mt-0.5">Classement automatique proposé</div>
              </div>
              <input ref={depotRef} type="file" accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={e => deposer(e.target.files?.[0])} />
            </div>
          )}
        </div>

        {/* Recent devis */}
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Devis récents</h2>
            <Link to="/devis" className="text-xs text-primary-500 hover:underline flex items-center gap-1">
              Voir tous <ArrowRight size={12} />
            </Link>
          </div>
          {recentDevis.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Aucun devis en cours
            </div>
          ) : (
            <div className="space-y-2">
              {recentDevis.map(d => (
                <Link
                  key={d.id}
                  to={`/devis/${d.id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      {d.enfant_prenom} {d.enfant_nom}
                    </div>
                    <div className="text-xs text-gray-500">{d.structure_nom} · {d.date_devis}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold text-gray-700">
                      {formatMontant(d.montant_mensuel)}/mois
                    </div>
                    <span className={getStatutBadgeClass(d.statut)}>
                      {getStatutLabel(d.statut)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* By structure */}
      {stats?.byStructure && canSeeFinancials && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Répartition par structure</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.byStructure.map(s => (
              <div key={s.structure_nom} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: s.couleur }}
                  />
                  <span className="text-sm font-medium text-gray-700">{s.structure_nom}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-bold text-gray-900">{formatMontant(s.total)}</div>
                    <div className="text-xs text-gray-400">{s.nb_factures} facture(s)</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle size={14} />{formatMontant(s.encaisse)} encaissé
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
